import CtaBannerV2 from "@/components/Home/v2/CtaBannerV2";
import FooterV2 from "@/components/Home/v2/FooterV2";
import NavbarV2 from "@/components/Home/v2/NavbarV2";
import Text from "@/components/Home/v2/shared/Text";
import GraphyHead from "@/components/organisms/GraphyHead";
import { homeV2Data } from "@/shared/data/home.v2.data";

type UpdateCategory = "Feature Launch" | "Feature Update" | "Product Announcement" | "Monthly Stats";

const CATEGORY_STYLES: Record<UpdateCategory, string> = {
  "Feature Launch": "bg-brandPrimary/10 text-brandPrimary",
  "Feature Update": "bg-brandPrimary/10 text-brandPrimary",
  "Product Announcement": "bg-green-100 text-green-700",
  "Monthly Stats": "bg-orange-100 text-orange-700",
};

function UpdateMeta({ category, date }: { category: UpdateCategory; date: string }) {
  return (
    <div className="flex flex-row items-center gap-3 lg:flex-col lg:items-start lg:gap-2 lg:pt-1">
      <span className={`rounded-full px-3 py-1 font-inter text-[11px] font-semibold uppercase tracking-widest whitespace-nowrap ${CATEGORY_STYLES[category]}`}>
        {category}
      </span>
      <span className="font-inter text-[13px] text-darkSlate04 whitespace-nowrap">{date}</span>
    </div>
  );
}

function Divider() {
  return <hr className="my-12 border-[#EBEBEB]" />;
}

export default function UpdatesPage() {
  return (
    <div className="home-v2-scope min-h-screen bg-white">
      <GraphyHead
        title="Graphy Updates — Feature Launches, Announcements & Stats"
        description="Stay up to date with the latest feature launches, product announcements, and creator milestones from Graphy."
        canonicalPath="/updates"
        countryCode=""
      />

      <NavbarV2 />

      {/* ── Hero ── */}
      <section className="w-full bg-white py-16 md:py-20 border-b border-[#EBEBEB]">
        <div className="container mx-auto max-w-5xl px-4 sm:px-6">
          <Text as="h1" variant="display-lg" className="text-neutralBlack">
            Product Updates
          </Text>
        </div>
      </section>

      {/* ── Updates ── */}
      <section className="w-full bg-white py-14 md:py-20">
        <div className="container mx-auto max-w-5xl px-4 sm:px-6">

          {/* AI Website Builder – Launch Plan pages */}
          <div className="flex flex-col gap-6 lg:flex-row lg:gap-16" id="ai-website-builder-launch-plan">
            <div className="w-full lg:w-52 lg:flex-shrink-0">
              <UpdateMeta category="Feature Update" date="June 10, 2025" />
            </div>
            <article className="min-w-0 flex-1">
              <Text as="h2" variant="display-sm" className="text-neutralBlack mb-4">
                AI Website Builder Now Lets Launch Plan Creators Add New Pages
              </Text>
              <Text as="p" variant="body-lg" className="text-darkSlate03">
                Launch Plan creators can now add up to 2 new pages in the AI Website Builder, no upgrade required. Pages can be deleted and recreated freely, as long as the total stays under 2.
              </Text>
            </article>
          </div>

          <Divider />

          {/* Memberships Learner Experience */}
          <div className="flex flex-col gap-6 lg:flex-row lg:gap-16" id="memberships-learner-experience">
            <div className="w-full lg:w-52 lg:flex-shrink-0">
              <UpdateMeta category="Feature Update" date="June 1, 2025" />
            </div>
            <article className="min-w-0 flex-1">
              <Text as="h2" variant="display-sm" className="text-neutralBlack mb-4">
                Memberships Post-Purchase Experience for Learners
              </Text>
              <Text as="p" variant="body-lg" className="text-darkSlate03 mb-4">
                Learners now have a complete membership experience on web, from discovery to access to renewal. A new Memberships tab in Learner Dashboard &gt; Library lets learners view all purchased memberships, browse included courses and blogs, manage their plan, and explore new memberships. Expired courses remain visible in the Courses tab with the correct access state.
              </Text>
              <Text as="p" variant="body-lg" className="text-darkSlate03">
                Admins can now cancel any learner&apos;s subscription from the membership Customers tab. The learner retains access until their next renewal date and shows as &ldquo;Scheduled for cancellation.&rdquo; Note: on website v1, new members-only blogs must be manually added to the relevant membership. Android and iOS support coming soon.
              </Text>
            </article>
          </div>

          <Divider />

          {/* Multiple Memberships */}
          <div className="flex flex-col gap-6 lg:flex-row lg:gap-16" id="multiple-memberships">
            <div className="w-full lg:w-52 lg:flex-shrink-0">
              <UpdateMeta category="Feature Update" date="May 13, 2025" />
            </div>
            <article className="min-w-0 flex-1">
              <Text as="h2" variant="display-sm" className="text-neutralBlack mb-4">
                Multiple Memberships &amp; Recurring Subscription
              </Text>
              <Text as="p" variant="body-lg" className="text-darkSlate03 mb-4">
                Creators can now run multiple memberships in parallel, each with its own courses, pricing plans, and landing page. Supports up to 5 pricing tiers per membership (Free, OTP, or Subscription) with monthly, quarterly, yearly, and custom intervals.
              </Text>
              <Text as="p" variant="body-lg" className="text-darkSlate03">
                Indian creators can enable recurring autopay via Cashfree. Memberships can be paused or archived without affecting existing subscribers. Promo codes, campaign filters, transaction reports, and the website editor have all been updated to support the new membership structure.
              </Text>
            </article>
          </div>

          {/* Add future updates here, separated by <Divider /> */}

        </div>
      </section>

      <CtaBannerV2 {...homeV2Data.ctaBanner} />
      <FooterV2 {...homeV2Data.footer} />
    </div>
  );
}
